import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Typography, Chip, LinearProgress, IconButton, Button, useTheme, useMediaQuery, FormControlLabel, Switch, alpha, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import Grid from '@mui/material/Grid2';
import EventsStatusCard from './components/EventsStatus/EventsStatusCard';
import UpcomingEventCard from './components/UpcomingEvent/UpcomingEventCard';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { MagicStar } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { between, colCenter } from '@ui/style/style';
import { StyledMenu } from '@ui/components/common/Menu';
import { useEffect, useMemo, useState } from 'react';
import NewEventDialogDrawer from './NewEvent';
import TicketsDialog from './TicketsDialog';
import { useAppDispatch } from '@application/store/useStore';
import { deleteVDKEventAsync, getPartnerEventsAsync, LinkVDKEventAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { useSelector } from 'react-redux';
import { APIStatus } from '@application/enums/api-status';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import VDKEventAPI from '@infrastructure/VDK-event-api';
import { syncNetherlandsFixturesCurrentYearAsync } from '@application/features/football/footballThunk';
import { hasPermission } from '@application/helpers/permission-helper';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { EmailTemplateType } from '@application/features/emailTemplates/models/email-template-type';
import { DEFAULT_NEW_EVENT_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/invitationEmailDefaultGerman';
import { DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/ticketConfirmationEmailDefaultGerman';
import { DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/thankYouEmailDefautGerman';
import { DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/reinvitationreminderEmailDefaultGerman';
import { DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/registeredreminderEmailDefaultGerman';
import { DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/thankYouEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/ticketConfirmationEmailDutch';
import { DEFAULT_NEW_EVENT_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/invitationEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/thankYouEmailDefaultEng';
import { DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/reinvitationreminderEmailDefaultEng';
import { DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/reinvitationreminderEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/registeredreminderEmailDefaultEng';
import { DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/registeredreminderEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/invitationEmailDefaultEng';
import { DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/ticketConfirmationEmailDefaultEng';
// Final Details Email Templates
import { DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/finaldetailEmailDefaultEng';
import { DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/finaldetailEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/finaldetailEmailDefaultGerman';
// Post-Event Thank You Email Templates
import { DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/postEventThankYouEmailDefaultEng';
import { DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/postEventThankYouEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/postEventThankYouEmailDefaultGerman';
import { createDefaultEmailTemplatesAsync } from '@application/features/emailTemplates/emailTemplatesThunk';
// Helper function to build default email templates with specific body sections
const buildDefaultEmailTemplates = (languages) => {
    const templates = [];
    const templateConfigs = [];
    // Map language IDs to body content
    languages?.forEach((langObj) => {
        let invitationBody = DEFAULT_NEW_EVENT_EMAIL_BODY;
        let ticketBody = DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY;
        let thankYouBody = DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY;
        let reInvitationReminderBody = DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY;
        let registeredReminderBody = DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY;
        let finalDetailsBody = DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY;
        let postEventThankYouBody = DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY;
        let invitationTemplateName = `Invitation Email`;
        let welcomeTemplateName = `Thank You Email`;
        let ticketTemplateName = `Ticket Confirmation Email`;
        let reInvitationReminderTemplateName = `Re-Invitation Reminder Email`;
        let registeredReminderTemplateName = `Registered Reminder Email`;
        let finalDetailsTemplateName = `Final Details Email`;
        let postEventThankYouTemplateName = `Post-Event Thank You Email`;
        // Dutch templates
        if (langObj?.code?.toLowerCase() === 'nl' || langObj?.name?.toLowerCase().includes('dutch')) {
            invitationBody = DEFAULT_NEW_EVENT_EMAIL_BODY_DUTCH;
            ticketBody = DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_DUTCH;
            thankYouBody = DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_DUTCH;
            reInvitationReminderBody = DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_DUTCH;
            registeredReminderBody = DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_DUTCH;
            finalDetailsBody = DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_DUTCH;
            postEventThankYouBody = DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_DUTCH;
            invitationTemplateName = `Uitnodigingsmail`;
            welcomeTemplateName = `Bedankmail`;
            ticketTemplateName = `Ticketbevestigingsmail`;
            reInvitationReminderTemplateName = `Herinnering opnieuw uitnodigen`;
            registeredReminderTemplateName = `Geregistreerde herinnering`;
            finalDetailsTemplateName = `E-mail met definitieve details`;
            postEventThankYouTemplateName = `Bedankt voor uw bezoek-e-mail`;
        }
        // German templates
        else if (langObj?.code?.toLowerCase() === 'de' || langObj?.name?.toLowerCase().includes('german')) {
            invitationBody = DEFAULT_NEW_EVENT_EMAIL_BODY_GERMAN;
            ticketBody = DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_GERMAN;
            thankYouBody = DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_GERMAN;
            reInvitationReminderBody = DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_GERMAN;
            registeredReminderBody = DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_GERMAN;
            finalDetailsBody = DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_GERMAN;
            postEventThankYouBody = DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_GERMAN;
            invitationTemplateName = `Einladungs-E-Mail`;
            welcomeTemplateName = `Dankes-E-Mail`;
            ticketTemplateName = `Ticketbestätigungs-E-Mail`;
            reInvitationReminderTemplateName = `Erneute Einladungserinnerung`;
            registeredReminderTemplateName = `Registrierungserinnerung`;
            finalDetailsTemplateName = `Abschließende Detaillierungs-E-Mail`;
            postEventThankYouTemplateName = `Dankesmail nach der Veranstaltung`;
        }
        // Default to English
        templateConfigs.push({
            languageId: langObj?.id,
            invitationBody,
            ticketBody,
            thankYouBody,
            reInvitationReminderBody,
            registeredReminderBody,
            finalDetailsBody,
            postEventThankYouBody,
            invitationTemplateName,
            welcomeTemplateName,
            ticketTemplateName,
            reInvitationReminderTemplateName,
            registeredReminderTemplateName,
            finalDetailsTemplateName,
            postEventThankYouTemplateName
        });
    });
    // Create email templates for each language and template type
    templateConfigs.forEach((config) => {
        // Invitation template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.Invitation,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.invitationTemplateName,
            subjectTemplate: 'You are invited to {{sys:eventName}}',
            bodyHtmlTemplate: config.invitationBody,
            status: 1
        });
        // Ticket confirmation template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.Ticket,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: false,
            name: config.ticketTemplateName,
            subjectTemplate: 'Your ticket for {{sys:eventName}} is ready',
            bodyHtmlTemplate: config.ticketBody,
            status: 1
        });
        // Thank you template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.WelcomeThankYou,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.welcomeTemplateName,
            subjectTemplate: 'Thank you for attending {{sys:eventName}}',
            bodyHtmlTemplate: config.thankYouBody,
            status: 1
        });
        // Re-invitation reminder template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.ReInvitationReminder,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.reInvitationReminderTemplateName,
            subjectTemplate: 'You are re invited to {{sys:eventName}}',
            bodyHtmlTemplate: config.reInvitationReminderBody,
            status: 1
        });
        // Registered reminder template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.RegisteredReminder,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.registeredReminderTemplateName,
            subjectTemplate: 'Reminder: {{sys:eventName}} is coming up in 2 days!',
            bodyHtmlTemplate: config.registeredReminderBody,
            status: 1
        });
        // Final Details template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.FinalDetails,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.finalDetailsTemplateName,
            subjectTemplate: 'Final details for {{sys:eventName}}',
            bodyHtmlTemplate: config.finalDetailsBody,
            status: 1
        });
        // // Post-Event Thank You template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.PostEventThankYou,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.postEventThankYouTemplateName,
            subjectTemplate: 'Thank you for attending {{sys:eventName}}',
            bodyHtmlTemplate: config.postEventThankYouBody,
            status: 1
        });
    });
    return templates;
};
const EventsTab = ({ partnerId }) => {
    const { VDKEventForPartner, status } = useSelector((state) => state.VDKEvent);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const { languages } = useSelector((state) => state.languages);
    const canUpdatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Update');
    const canCreateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Create');
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    // Mobile menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [actionMenuRowId, setActionMenuRowId] = useState(null);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const handleActionMenuOpen = (event, rowId) => {
        setActionMenuAnchorEl(event.currentTarget);
        setActionMenuRowId(rowId);
    };
    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setActionMenuRowId(null);
    };
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const formatDate = (value) => {
        if (!value)
            return '-';
        const d = new Date(value);
        if (isNaN(d.getTime()))
            return '-';
        const options = { month: 'short', day: '2-digit', year: 'numeric' };
        return d.toLocaleDateString(undefined, options);
    };
    const upcomingFromApi = VDKEventForPartner?.upcomingEvent;
    const upcomingEvent = {
        eventName: upcomingFromApi?.title || t('sponsorships.noUpcomingEvent') || '-',
        eventDate: formatDate(upcomingFromApi?.startDate),
        eventTime: formatDate(upcomingFromApi?.endDate),
        avatarUrl: undefined
    };
    const [statusCounts, setStatusCounts] = useState({
        active: 0,
        inactive: 0
    });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [eventFromApi, setEventFromApi] = useState(false);
    const [syncingFootball, setSyncingFootball] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const syncingIconSx = {
        '@keyframes spin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' }
        },
        animation: syncingFootball ? 'spin 1s linear infinite' : 'none'
    };
    const computeCounts = (items) => {
        const norm = (s) => typeof s === 'string' ? s.toLowerCase() : String(s ?? '').toLowerCase();
        const linked = items.filter((e) => norm(e.status) === 'linked').length;
        const notLinked = items.length - linked;
        return { active: linked, inactive: Math.max(0, notLinked) };
    };
    useEffect(() => {
        const updateCounts = async () => {
            const items = VDKEventForPartner?.items || [];
            const total = VDKEventForPartner?.totalCount || items.length;
            // If we don't have all items for an accurate breakdown, fetch all items just for the chart.
            if (partnerId && total > items.length) {
                try {
                    const response = await VDKEventAPI.getPartnerVDKEvents({
                        pageSize: total,
                        pageNumber: 1,
                        partnerId,
                        isEventFromApi: eventFromApi,
                        searchTerm: searchTerm || undefined
                    });
                    const allItems = response.data?.items || [];
                    setStatusCounts(computeCounts(allItems));
                    return;
                }
                catch {
                    // Fallback to current page items if the bulk fetch fails
                }
            }
            setStatusCounts(computeCounts(items));
        };
        void updateCounts();
    }, [
        VDKEventForPartner?.items,
        VDKEventForPartner?.totalCount,
        partnerId,
        eventFromApi,
        searchTerm
    ]);
    const { PartnerEvent } = useSelector((state) => state.partnerEvent);
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    const dispatch = useAppDispatch();
    const handleSyncFootballCurrentYear = async () => {
        if (syncingFootball)
            return;
        setSyncingFootball(true);
        try {
            await dispatch(syncNetherlandsFixturesCurrentYearAsync(partnerId)).unwrap();
            // After sync, refresh partner events from API view.
            const request = {
                pageSize: paginationModel.pageSize,
                pageNumber: paginationModel.page + 1,
                partnerId: partnerId,
                isEventFromApi: true
            };
            setEventFromApi(true);
            dispatch(getPartnerEventsAsync(request));
        }
        catch (e) {
            // no toaster for sync (intentional)
        }
        finally {
            setSyncingFootball(false);
        }
    };
    const handleLinkToEvent = (eventid) => {
        if (!canUpdatePartners) {
            toast.error(t('common.unauthorized') || 'You do not have permission to link events');
            return;
        }
        if (eventid == null)
            return;
        dispatch(LinkVDKEventAsync(eventid))
            .unwrap()
            .then(async (response) => {
            if (response.isSuccess) {
                // Create default email templates for the new event
                const defaultTemplates = buildDefaultEmailTemplates(languages);
                await dispatch(createDefaultEmailTemplatesAsync({
                    eventId: response.data?.id,
                    templates: defaultTemplates
                })).unwrap()
                    .catch((templateError) => {
                    console.warn('Failed to create default email templates:', templateError);
                });
                const successMessage = response.message || t('events.linkedsuccessfully') || 'Event linked successfully!';
                toast.success(successMessage);
            }
            else {
                const errorMessage = response.message || t('events.linkingFailed') || 'Event linking failed!';
                toast.error(errorMessage);
            }
            //getAllPartnerEvents();
        });
    };
    const handleDeleteEvent = (eventid) => {
        if (eventid == null)
            return;
        dispatch(deleteVDKEventAsync(eventid))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                const successMessage = response.message ||
                    t('events.eventdeletedSuccessfully') ||
                    'Event deleted successfully!';
                toast.success(successMessage);
            }
            else {
                const errorMessage = response.message || t('events.deletionFailed') || 'Event deletion failed!';
                toast.error(errorMessage);
            }
            //getAllPartnerEvents();
        });
    };
    const handleEditEvent = (eventid, eventStatus) => {
        if (!canUpdatePartners && !canUpdateEvents) {
            toast.error(t('common.unauthorized') || 'You do not have permission to edit events');
            return;
        }
        if (eventid === null)
            return;
        setIsEdit(true);
        seteditEventId(eventid);
        if (eventStatus.toLowerCase() === 'linked')
            navigate(`/create-event?eventId=${eventid}`);
        setNewEventOpen(true);
    };
    // const getAllPartnerEvents = () =>
    // {
    //   if (partnerId) {
    //     const request: GetPartnerVDKEventRequest = {
    //       pageSize: paginationModel.pageSize,
    //       pageNumber: paginationModel.page + 1,
    //       partnerId: partnerId,
    //       isEventFromApi: eventFromApi
    //     };
    //     dispatch(getPartnerEventsAsync(request));
    //   }
    // }
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0 && partnerId) {
            const request = {
                pageSize: paginationModel.pageSize,
                pageNumber: paginationModel.page + 1,
                partnerId: partnerId,
                isEventFromApi: eventFromApi,
                searchTerm: searchTerm || undefined
            };
            dispatch(getPartnerEventsAsync(request));
        }
    }, [
        dispatch,
        partnerId,
        paginationModel.page,
        paginationModel.pageSize,
        eventFromApi,
        searchTerm,
        selectedCompanyId,
        PartnerEvent
    ]);
    const eventGridRows = useMemo(() => (VDKEventForPartner?.items || []).map((event, index) => ({
        id: event.id || index,
        eventName: event.title,
        eventDate: event.startDate,
        eventTime: event.endDate,
        eventUrl: event.eventURl,
        type: event.benefitType,
        quantity: event.quantity,
        status: event.status,
        benefitUtilization: event.benefitUtilization ?? 0,
        totalTickets: event.totalTickets ?? 0,
        assignedTickets: event.assignedTickets ?? 0,
        location: event.location ?? '',
        country: '',
        interests: event.interests || [],
        assigned: event.assignees || [],
        isSponsorPartnerExternalEvent: event.isSponsorPartnerExternalEvent ?? false
    })), [VDKEventForPartner?.items]);
    const eventGridColumns = [
        {
            field: 'event',
            headerName: t('sponsorships.eventsTab.tableHeaders.event') || 'Event',
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => (_jsxs(Box, { flex: 1, minWidth: 0, children: [_jsx(Typography, { variant: 'body2', fontWeight: 400, noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: params.row.eventName }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: (() => {
                            const start = params.row.eventDate ? new Date(params.row.eventDate) : undefined;
                            const end = params.row.eventTime ? new Date(params.row.eventTime) : undefined;
                            const format = (d) => d && !isNaN(d.getTime())
                                ? `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`
                                : '-';
                            const formattedStart = format(start);
                            const formattedEnd = format(end);
                            return `${formattedStart} → ${formattedEnd}`;
                        })() })] }))
        },
        {
            field: 'linked',
            headerName: t('sponsorships.eventsTab.tableHeaders.apiEvent') || 'API Event',
            flex: 0.5,
            minWidth: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const isLinked = params.row.isSponsorPartnerExternalEvent === true;
                const displayText = isLinked ? t('common.yes') || 'Yes' : t('common.no') || 'No';
                return (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: displayText }));
            }
        },
        {
            field: 'type',
            headerName: t('sponsorships.eventsTab.tableHeaders.type') || 'Type',
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: params.value }))
        },
        {
            field: 'quantity',
            headerName: t('sponsorships.eventsTab.tableHeaders.quantity') || 'Quantity',
            flex: 0.5,
            minWidth: 100,
            align: 'right',
            headerAlign: 'center',
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: params.value }))
        },
        {
            field: 'status',
            headerName: t('sponsorships.eventsTab.tableHeaders.status') || 'Status',
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => {
                const statusLower = params.value?.toLowerCase() || '';
                const translatedStatus = statusLower === 'linked'
                    ? t('common.linked') || 'Linked'
                    : t('common.notLinked') || 'Not Linked';
                return (_jsx(Chip, { variant: 'soft', label: translatedStatus, size: 'medium', color: statusLower === 'linked' ? 'primary' : 'secondary', sx: statusLower !== 'linked'
                        ? {
                            backgroundColor: theme.palette.grey[200],
                            color: theme.palette.text.secondary
                        }
                        : undefined }));
            }
        },
        {
            field: 'benefitUtilization',
            headerName: t('sponsorships.eventsTab.tableHeaders.ticketUsage') || 'Ticket Usage',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                const assigned = params.row.assignedTickets || 0;
                const total = params.row.totalTickets || 0;
                const percentage = params.value || 0;
                return (_jsx(Tooltip, { title: `${assigned}/${total}`, arrow: true, placement: 'top', children: _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, width: '100%', children: [_jsx(LinearProgress, { variant: 'determinate', value: percentage, sx: {
                                    flexGrow: 1,
                                    height: '4px',
                                    borderRadius: '500px',
                                    backgroundColor: percentage > 0
                                        ? alpha(theme.palette.primary.main, 0.24)
                                        : alpha(theme.palette.text.primary, 0.24),
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: '500px',
                                        backgroundColor: percentage > 0 ? theme.palette.primary.main : theme.palette.text.primary
                                    }
                                } }), _jsxs(Typography, { variant: 'caption', color: 'text.secondary', fontWeight: 400, children: [percentage, "%"] })] }) }));
            }
        },
        {
            field: 'location',
            headerName: t('labels.location') || 'Location',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: params.value || '—' }))
        },
        {
            field: 'interests',
            headerName: t('labels.tags') || 'Tags',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const { interests } = params.row; // assuming 'interests' is part of row data
                return (_jsx(Tooltip, { title: _jsx(Box, { sx: { p: 0.5 }, children: interests.map((interest, index) => (_jsx(Typography, { variant: 'body2', sx: { py: 0.25 }, children: interest.title }, index))) }), arrow: true, placement: 'bottom', children: _jsx(Chip, { label: String(interests.length).padStart(2, '0'), size: 'small', sx: { fontWeight: 400, borderRadius: '500px', cursor: 'pointer' }, variant: 'soft', color: 'default' }) }));
            }
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: 'small', onClick: (e) => {
                            e.stopPropagation();
                            handleActionMenuOpen(e, params.row.id);
                        }, children: _jsx(MoreHorizIcon, { fontSize: 'small' }) }), actionMenuRowId === params.row.id && (_jsx(StyledMenu, { anchorEl: actionMenuAnchorEl, open: Boolean(actionMenuAnchorEl), onClose: handleActionMenuClose, onClick: (e) => e.stopPropagation(), actions: [
                            {
                                text: t('sponsorships.eventsTab.actions.edit') || 'Edit',
                                group: true,
                                disabled: !(canUpdatePartners || canUpdateEvents),
                                to: () => {
                                    if (canUpdatePartners || canUpdateEvents) {
                                        handleEditEvent(params.row.id, params.row.status);
                                        handleActionMenuClose();
                                    }
                                }
                            },
                            {
                                text: t('sponsorships.eventsTab.actions.delete') || 'Delete',
                                group: true,
                                disabled: !canUpdatePartners,
                                to: () => {
                                    if (canUpdatePartners) {
                                        handleDeleteEvent(params.row.id);
                                        handleActionMenuClose();
                                    }
                                }
                            },
                            {
                                text: t('sponsorships.eventsTab.actions.linkToHappinessEvent') ||
                                    'Link to happiness event',
                                disabled: !canUpdatePartners || params.row.status.toLowerCase() === 'linked',
                                to: () => {
                                    if (canUpdatePartners && params.row.status.toLowerCase() !== 'linked') {
                                        handleLinkToEvent(params.row.id);
                                        handleActionMenuClose();
                                    }
                                }
                            }
                        ] }))] }))
        }
    ];
    // New Event dialog/drawer state
    const [newEventOpen, setNewEventOpen] = useState(false);
    const handleNewEventOpen = () => {
        setIsEdit(false);
        setNewEventOpen(true);
    };
    const handleNewEventClose = () => setNewEventOpen(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editEventId, seteditEventId] = useState('');
    return (_jsxs(Box, { children: [_jsxs(Grid, { container: true, spacing: 2, gap: 2, children: [_jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(UpcomingEventCard, { ...upcomingEvent }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(EventsStatusCard, { activeCount: VDKEventForPartner?.activeEvents || 0, inactiveCount: VDKEventForPartner?.inactiveEvents || 0 }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(PrimaryGrid, { rows: eventGridRows, maxHeight: 'calc(100dvh - 386px)', sx: {
                                height: 'calc(100dvh - 386px)'
                            }, columns: eventGridColumns, paginationMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: VDKEventForPartner?.totalCount || 0, loading: status === APIStatus.PENDING, showFilters: false, showHeaderControls: false, onCellClick: (params) => params.row.id &&
                                navigate(`/sponsorship-details/${partnerId}/ticket-detail/${params.row.id}`), showNewHeaderControls: true, customButton: _jsxs(Box, { sx: {
                                    ...(!mobileSearchOpen && between),
                                    alignItems: isMobile ? undefined : 'center',
                                    pb: '12px'
                                }, children: [!mobileSearchOpen && (_jsx(Box, { sx: { ...colCenter, gap: 1 }, children: _jsxs(Typography, { variant: 'h5', color: 'text.primary', children: [VDKEventForPartner?.totalCount || 0, " ", t('common.events')] }) })), _jsx(Box, { sx: {
                                            ...colCenter,
                                            gap: 1.5,
                                            pb: 0
                                        }, children: isMobile ? (_jsxs(_Fragment, { children: [!mobileSearchOpen && (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: 'small', color: 'inherit', onClick: handleMenuOpen, children: _jsx(MoreHorizIcon, {}) }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: [
                                                                {
                                                                    text: t('common.sync') || 'Sync',
                                                                    icon: _jsx(SyncOutlinedIcon, { fontSize: 'small', sx: syncingIconSx }),
                                                                    disabled: !canUpdatePartners,
                                                                    to: async () => {
                                                                        if (canUpdatePartners) {
                                                                            await handleSyncFootballCurrentYear();
                                                                            handleMenuClose();
                                                                        }
                                                                    }
                                                                },
                                                                {
                                                                    text: t('events.newEvent'),
                                                                    disabled: !canUpdatePartners || !canCreateEvents,
                                                                    to: () => {
                                                                        if (canUpdatePartners && canCreateEvents) {
                                                                            handleNewEventOpen();
                                                                            handleMenuClose();
                                                                        }
                                                                    }
                                                                },
                                                                {
                                                                    text: t('sponsorships.eventsFromAPIS'),
                                                                    isSwitch: true,
                                                                    icon: (_jsx(Switch, { color: 'primary', checked: eventFromApi, onChange: (e) => {
                                                                            setEventFromApi(e.target.checked);
                                                                        } })),
                                                                    to: () => {
                                                                        handleMenuClose();
                                                                    }
                                                                }
                                                            ] })] })), _jsx(SearchFilter, { isDatagrid: true, mobileSearchOpen: mobileSearchOpen, setMobileSearchOpen: setMobileSearchOpen, searchValue: searchTerm, onSearchChange: setSearchTerm, placeholder: t('common.search') || 'Search...' })] })) : (_jsx(_Fragment, { children: _jsxs(Box, { display: 'flex', alignItems: 'center', gap: '12px', children: [_jsx(Box, { display: 'flex', bgcolor: 'white', borderRadius: '16px', gap: '10px', px: theme.spacing(2), height: '48px', sx: {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.2)
                                                        }, children: _jsx(FormControlLabel, { control: _jsx(Switch, { color: 'primary', checked: eventFromApi, onChange: (e) => {
                                                                    setEventFromApi(e.target.checked);
                                                                } }), label: _jsx("span", { style: { ...theme.typography.body2, fontWeight: 500 }, children: t('sponsorships.eventsFromAPIS') }) }) }), _jsx(Tooltip, { title: t('common.sync') || 'Sync', children: _jsx("span", { children: _jsx(IconButton, { size: 'large', color: 'inherit', disabled: syncingFootball || !canUpdatePartners, onClick: () => {
                                                                    if (canUpdatePartners) {
                                                                        void handleSyncFootballCurrentYear();
                                                                    }
                                                                }, children: _jsx(SyncOutlinedIcon, { sx: syncingIconSx }) }) }) }), _jsx(SearchFilter, { searchValue: searchTerm, onSearchChange: setSearchTerm, placeholder: t('common.search') || 'Search...' }), _jsx(Button, { size: 'large', variant: 'outlined', disabled: !canUpdatePartners || !canCreateEvents, color: 'primary', startIcon: _jsx(MagicStar, { variant: 'Outline' }), onClick: () => {
                                                            if (canUpdatePartners && canCreateEvents) {
                                                                handleNewEventOpen();
                                                            }
                                                        }, children: t('events.newEvent') })] }) })) })] }) }) })] }), _jsx(NewEventDialogDrawer, { open: newEventOpen, onClose: handleNewEventClose, isEdit: isEdit, eventId: editEventId }), _jsx(TicketsDialog, { open: ticketsDialogOpen, onClose: () => setTicketsDialogOpen(false) })] }));
};
export default EventsTab;
